/**
 * Diagnosis State Service - API layer for diagnosis state management
 */

import {
  SavedDiagnosisState,
  DiagnosisStateData,
  CreateStateRequest,
} from '../types/diagnosisState.types';
import {
  createDiagnosisState,
  updateDiagnosisState,
  deleteDiagnosisState,
} from './rogerDocApi';
import { useStore } from '../store';

/**
 * Create a new diagnosis state
 */
export const createState = async (
  diagnosisId: string,
  name: string,
  data: DiagnosisStateData
): Promise<SavedDiagnosisState> => {
  const user = useStore.getState().user;
  const request: CreateStateRequest = {
    name,
    primary_diagnosis: data.primary_diagnosis,
    secondary_diagnoses: data.secondary_diagnoses,
    case_mix_data: data.case_mix_data,
    dashboard_user_email: user?.email || null,
  };

  return await createDiagnosisState(diagnosisId, request);
};

/**
 * Update an existing diagnosis state
 */
export const saveState = async (
  diagnosisId: string,
  stateId: string,
  name: string,
  data: DiagnosisStateData
): Promise<SavedDiagnosisState> => {
  const user = useStore.getState().user;
  const request: CreateStateRequest = {
    name,
    primary_diagnosis: data.primary_diagnosis,
    secondary_diagnoses: data.secondary_diagnoses,
    case_mix_data: data.case_mix_data,
    dashboard_user_email: user?.email || null,
  };

  return await updateDiagnosisState(diagnosisId, stateId, request);
};

/**
 * Delete a diagnosis state
 */
export const deleteState = async (
  diagnosisId: string,
  stateId: string
): Promise<void> => {
  const user = useStore.getState().user;
  await deleteDiagnosisState(diagnosisId, stateId, user?.email || undefined);
};

/**
 * Utility function to check if two diagnosis state data objects are equal
 */
export const areStatesEqual = (
  state1: DiagnosisStateData | null,
  state2: DiagnosisStateData | null
): boolean => {
  if (!state1 || !state2) return false;

  // Deep comparison using JSON stringify
  // This is safe for our use case since we're comparing serializable data
  return JSON.stringify(state1) === JSON.stringify(state2);
};
